'use client'

/**
 * global-error.tsx (R90 2026-05-18, härter R90.3).
 *
 * Letzte Verteidigungslinie: rendert wenn auch das root-layout crasht.
 * Next.js-App-Router-Konvention. Diese Datei MUSS html+body selbst rendern.
 *
 * R90.3: Crash-Loop-Detection. Wenn binnen 30 s mehr als 2 Crashes auftreten,
 * fuehren wir einen HARD-RESET durch (SW unregistrieren + alle Caches loeschen
 * + reload). Das bricht stale-Bundle-Loops auf — typischer Pre-Launch-Bug
 * wenn der Browser noch das alte JS-Bundle gegen den neuen Build laeuft.
 */

import { useEffect } from 'react'

const CRASH_KEY = 'fgl_crash_log_v1'
const CRASH_WINDOW_MS = 30_000
const CRASH_LIMIT_FOR_HARD_RESET = 3

// 2026-06-20: ChunkLoadError = eindeutig Stale-Bundle. Sofort hard-reset beim
// ersten Crash (analog error.tsx). Verhindert dass User auf Crash-Screen stranden
// nachdem ein Deploy die Chunk-Hashes rotiert hat (Stéphane-Bug 2cprp8hl).
// r19.55.95 (2026-08-06): "Connection closed." + generisches "Failed to fetch"
// ergänzt — 9 Auto-Reports mit Chunk-Hash 05f_5nzcc6vub aus altem Deploy →
// HTTP/2-Stream vom 404 geschlossen. Selber Root-Cause, andere Message.
function isChunkLoadError(error: { name?: string; message?: string; stack?: string }): boolean {
  const blob = `${error?.name || ''} ${error?.message || ''} ${error?.stack || ''}`
  return /ChunkLoadError|Loading chunk \d+ failed|Failed to (load|fetch) (chunk|module)|Failed to fetch|Connection closed\.|Importing a module script failed|webpackChunk/i.test(blob)
}

async function hardReset() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)))
    }
  } catch {}
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)))
    }
  } catch {}
  try { sessionStorage.clear() } catch {}
  try { localStorage.removeItem(CRASH_KEY) } catch {}
  // Cache-Buster anhaengen damit Browser nicht aus dem Disk-Cache holt
  const url = new URL(window.location.href)
  url.searchParams.set('_hard_reset', String(Date.now()))
  window.location.replace(url.toString())
}

function trackCrashAndMaybeReset(): boolean {
  try {
    const now = Date.now()
    const raw = localStorage.getItem(CRASH_KEY)
    const arr: number[] = raw ? JSON.parse(raw) : []
    const fresh = arr.filter((t) => now - t < CRASH_WINDOW_MS)
    fresh.push(now)
    localStorage.setItem(CRASH_KEY, JSON.stringify(fresh))
    if (fresh.length >= CRASH_LIMIT_FOR_HARD_RESET) {
      void hardReset()
      return true
    }
  } catch {}
  return false
}

// R91.10.4 (2026-05-19): Dedupe + Throttle. Zwischen 2026-05-18 und -19 sind 219
// Auto-Reports mit „[AUTO] Root-Layout-Crash" entstanden — fast alle wegen
// React #300 Hydration-Mismatch + Concurrent-Mode-Re-Render-Loop. Pro Crash
// feuerte das useEffect mehrfach (jeder Boundary-Re-Render = neuer POST).
// Fix: per Browser-Session nur 1 Report pro (digest|message|pfad) in 10 min.
const REPORT_KEY = 'fgl_autoreport_log_v1'
const REPORT_DEDUPE_MS = 10 * 60 * 1000

function fingerprintCrash(error: { digest?: string; message?: string }, path: string): string {
  return `${error?.digest || ''}|${(error?.message || '').slice(0, 100)}|${path}`
}

function shouldSendReport(fp: string): boolean {
  try {
    const now = Date.now()
    const raw = localStorage.getItem(REPORT_KEY)
    const map: Record<string, number> = raw ? JSON.parse(raw) : {}
    const last = map[fp] || 0
    if (now - last < REPORT_DEDUPE_MS) return false
    map[fp] = now
    // Aufraeumen: nur Eintraege der letzten 24 h behalten
    const cutoff = now - 24 * 60 * 60 * 1000
    for (const k of Object.keys(map)) if (map[k] < cutoff) delete map[k]
    localStorage.setItem(REPORT_KEY, JSON.stringify(map))
    return true
  } catch {
    return true // bei localStorage-Disabled lieber Report senden als gar nichts
  }
}

export default function RootError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const chunkError = isChunkLoadError(error)

  useEffect(() => {
    if (chunkError) {
      // r19.55.215: Kein Auto-Report mehr fuer Standard-Stale-Bundle. Nur bei
      // Loop-Verdacht (`_hard_reset` bereits in URL = Reset hat nicht geholfen)
      // wird ein Report als HIGH gesendet. Ansonsten still hard-resetten.
      let inLoop = false
      try {
        inLoop = !!new URL(window.location.href).searchParams.get('_hard_reset')
      } catch {}
      if (inLoop) {
        try {
          const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
          fetch('/api/bug-reports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: `[AUTO] Stale-Bundle LOOP (root) · ${path.slice(0, 60)}`,
              description: `Hard-Reset lief bereits — root-Layout-Crash wiederholt sich. Echtes Problem, nicht nur veralteter Chunk.\nPfad: ${path}\nMessage: ${error?.message || '—'}\nDigest: ${error?.digest || '—'}`,
              severity: 'HIGH', kind: 'BUG', source: 'WEB',
              url: typeof window !== 'undefined' ? window.location.href : null,
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
              appVersion: 'web-auto-stale-bundle-root-loop',
            }),
          }).catch(() => {})
        } catch {}
      }
      void hardReset()
      return
    }
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
      const fp = fingerprintCrash(error, path)
      if (!shouldSendReport(fp)) {
        // Stiller Skip: gleicher Crash wurde in den letzten 10 min schon gemeldet
        console.warn('[RootError] dedupe-skip', fp)
      } else {
        fetch('/api/bug-reports', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: `[AUTO] Root-Layout-Crash · ${path.slice(0, 60)}`,
            description: [
              `Auto-Report vom Root-Error-Boundary (R91.10.4: deduped 10min/fingerprint)`,
              `Pfad: ${path}`,
              `Digest: ${error?.digest || '—'}`,
              `Message: ${error?.message || '—'}`,
              `Stack: ${(error?.stack || '').slice(0, 2500)}`,
            ].join('\n'),
            severity: 'CRITICAL', kind: 'BUG', source: 'WEB',
            url: typeof window !== 'undefined' ? window.location.href : null,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            appVersion: 'web-auto-report',
          }),
        }).catch(() => {})
      }
    } catch {}
    console.error('[RootError]', error)
    trackCrashAndMaybeReset()
  }, [error, chunkError])

  if (chunkError) {
    // 2026-06-20: global-error.tsx ist die LETZTE Verteidigungslinie und rendert
    // wenn das Root-Layout (mit Theme-CSS-Variablen) NICHT durchgekommen ist.
    // CSS-Vars sind dann möglicherweise nicht definiert — also explizite Dark-
    // Fallback-Hex hier OK, weil das Crash-Screen für 1-2 Sekunden ist bevor
    // hardReset() den Browser komplett neu lädt. Keine Theme-Anwendung mehr nötig.
    return (
      <html lang="de">
        <body style={{ margin: 0, fontFamily: 'system-ui, sans-serif', background: '#0d1117', color: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>⟳</div>
            <h1 style={{ fontSize: '22px', fontWeight: 900, margin: '0 0 8px', color: '#f2c94e' }}>
              Neue Version verfügbar
            </h1>
            <p style={{ fontSize: '14px', color: '#8b949e', margin: 0, lineHeight: 1.6 }}>
              Wir laden die Plattform für dich frisch. Einen Moment — du musst nichts tun.
            </p>
          </div>
        </body>
      </html>
    )
  }

  return (
    <html lang="de">
      <body style={{ margin: 0, fontFamily: 'system-ui, sans-serif', background: '#0d1117', color: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ maxWidth: '420px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h1 style={{ fontSize: '24px', fontWeight: 900, margin: '0 0 8px' }}>
            FREE GLIDE LEAGUE
          </h1>
          <p style={{ fontSize: '14px', color: '#8b949e', margin: '0 0 24px', lineHeight: 1.6 }}>
            Tut uns leid — die Plattform hat sich gerade verschluckt. Wir haben es
            automatisch gemeldet.
          </p>
          {error?.digest && (
            <code style={{ display: 'block', fontSize: '10px', marginBottom: '16px', padding: '6px 12px', borderRadius: '4px', background: '#161b22', color: '#8b949e' }}>
              Ref: {error.digest}
            </code>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button onClick={() => reset()}
              style={{ background: 'rgba(242,201,78,0.15)', color: '#f2c94e', border: '1px solid rgba(242,201,78,0.4)', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}>
              Nochmal versuchen
            </button>
            <button onClick={() => { void hardReset() }}
              style={{ background: 'transparent', color: '#8b949e', border: '1px solid #30363d', padding: '8px 16px', borderRadius: '8px', fontWeight: 500, cursor: 'pointer', fontSize: '12px' }}>
              Hart neu laden (Cache leeren)
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
